import { computed, ref, toValue, watch, type MaybeRefOrGetter } from 'vue'
import type { SearchEngineLocation } from '~/composables/types/searchEngine'
import { useAlgoliaIndexNames } from '~/composables/algolia/useAlgoliaIndexNames'
import { HITS_PER_PAGINATION } from '~/composables/algolia/constants'
import { localitySlugForAlgoliaOptionalFilter } from '~/utils/localitySlugMatch'
import { buildAlgoliaAnalyticsTags } from '~/utils/searchEngineHelpers'
import { applyAlgoliaSearchAnalyticsGuard } from '~/utils/algoliaSearchAnalytics'
import {
  useSearchIndex,
  type TypesenseSearchOverrides,
} from '~/composables/search/useSearchIndex'
import type { AlgoliaIndexSearchParams } from '~/composables/algolia/useAlgoliaIndexSearch'
import { resolveLocalizedName } from '~/utils/resolveLocalizedName'
import { useSearchEngine } from '~/composables/search/useSearchEngine'
import { useTypesenseGeoSort } from '~/composables/useTypesenseGeoSort'
import { buildTypesenseGeoSort, type GeoCoords } from '~/utils/typesenseGeo'
import {
  TYPO_PROFILES,
  typesenseTypoParams,
} from '~/utils/typesenseTypoTolerance'

const LOCALITY_QUERY_BY = 'name,slug'

// Rank locations with more upcoming events first — Algolia has no equivalent
// here (this only affects the typesense engine; the default useSearchIndex
// pagination-only params are otherwise unchanged). Overrides typesense's
// default text-match ranking entirely, so a weak partial match with more
// events can outrank a closer match with fewer — acceptable for a locality
// picker, where "cities with more to do" is more useful than exact-match
// purity, but worth knowing if this ever needs a hybrid text+count sort.
const appliesGeoSort = (query: string, hasSlug?: boolean) => !query && !hasSlug

const toTypesenseLocalityParams = (
  requestOptions: AlgoliaIndexSearchParams['requestOptions'],
  query: string,
  geo?: { coords?: GeoCoords | null; hasSlug?: boolean }
): TypesenseSearchOverrides => {
  const paginationOptions = requestOptions as
    | { hitsPerPage?: number; page?: number }
    | undefined
  // Distance-sort the default "cities near you" list (no slug, no typed query)
  // when coords are available — the Typesense equivalent of the Algolia
  // aroundLatLngViaIP condition (KFT-4116), breaking ties by event count.
  // Any other case keeps the event-count ranking.
  const geoSort = appliesGeoSort(query, geo?.hasSlug)
    ? buildTypesenseGeoSort(geo?.coords, 'event_count:desc')
    : undefined
  return {
    q: query || '*',
    page: (paginationOptions?.page ?? 0) + 1,
    per_page: paginationOptions?.hitsPerPage ?? HITS_PER_PAGINATION,
    sort_by: geoSort ?? 'event_count:desc',
    ...typesenseTypoParams(LOCALITY_QUERY_BY, TYPO_PROFILES.locations),
  }
}

export const useSearchEngineLocalities = async (
  initialLocationSlug: MaybeRefOrGetter<string> = '',
  immediate = true
) => {
  const searchQuery = ref('')
  const locationSlug = ref(toValue(initialLocationSlug))

  watch(
    () => toValue(initialLocationSlug),
    (v) => {
      locationSlug.value = v ?? ''
    }
  )

  const {
    public: { appEnv },
  } = useRuntimeConfig()
  const defaultReqOpts = {
    similarQuery: '',
    clickAnalytics: true,
    hitsPerPage: 20,
    analyticsTags: buildAlgoliaAnalyticsTags(appEnv),
  }
  const { locations: locationsIndex } = useAlgoliaIndexNames()
  const { locale } = useI18n()

  // KFT-4116: viewer coordinates for the Typesense proximity default, replacing
  // Algolia's aroundLatLngViaIP. Client-only and Typesense-only; null on SSR /
  // Algolia / before geo resolves, so the picker still renders event-count
  // ranked without it.
  const { coords: geoCoords, resolveGeoCoords } = useTypesenseGeoSort()
  // Reactive (KFT-4115): an A/B force can switch the engine after hydration.
  // clientOnly: the only caller (Filters) sits in <ClientOnly>, so this never
  // runs during SSR/hydration — use the forced engine from the first request
  // instead of querying Algolia and then refetching from Typesense (KFT-4167).
  const searchEngine = useSearchEngine({ clientOnly: true })
  watch(
    searchEngine,
    (engine) => {
      if (import.meta.client && engine === 'typesense') {
        resolveGeoCoords()
      }
    },
    { immediate: true }
  )

  const hasSlug = computed(() =>
    Boolean(localitySlugForAlgoliaOptionalFilter(locationSlug.value))
  )

  // Geo only reaches the query when appliesGeoSort() holds, so only then may
  // it trigger a refetch — otherwise (e.g. a city page, where the slug is set)
  // geo resolving would re-run an identical request.
  const effectiveGeoCoords = computed(() =>
    appliesGeoSort(searchQuery.value, hasSlug.value) ? geoCoords.value : null
  )

  const createLocalitiesSearch = () =>
    useSearchIndex<SearchEngineLocation>(locationsIndex, {
      typesenseQueryBy: LOCALITY_QUERY_BY,
      typesenseCache: true,
      clientOnly: true,
      toTypesenseParams: (requestOptions, query) =>
        toTypesenseLocalityParams(requestOptions, query, {
          coords: geoCoords.value,
          hasSlug: hasSlug.value,
        }),
    })

  // The search instance snapshots the engine, so rebuild it on switch — sync,
  // so it's in place before useAsyncData's own `watch` (below) refetches.
  const localitiesSearch = shallowRef(createLocalitiesSearch())
  watch(
    searchEngine,
    () => {
      localitiesSearch.value = createLocalitiesSearch()
    },
    { flush: 'sync' }
  )
  const result = computed(() => localitiesSearch.value.result.value)

  const searchEngineLocalities = computed(() => {
    const raw = result.value
    if (!raw) return raw
    return {
      ...raw,
      hits: raw.hits.map((hit: SearchEngineLocation) => ({
        ...hit,
        name: resolveLocalizedName(hit.name, locale.value),
      })),
    }
  })

  const { execute } = await useAsyncData(
    locationsIndex,
    () => {
      const slugOpt = localitySlugForAlgoliaOptionalFilter(locationSlug.value)
      return localitiesSearch.value.search({
        query: searchQuery.value,
        requestOptions: applyAlgoliaSearchAnalyticsGuard(
          {
            ...defaultReqOpts,
            optionalFilters: [
              // give priority to localities if no slug is passed
              ...(!slugOpt ? ['type:locality'] : []),
              ...(slugOpt ? [`slug:${slugOpt}`] : []),
            ],
            // search around user's location if no slug is passed
            aroundLatLngViaIP:
              !slugOpt && import.meta.client && !searchQuery.value,
          },
          searchQuery.value
        ),
      })
    },
    {
      immediate,
      deep: true,
      // geoCoords (KFT-4116) resolves client-side after mount; re-run so the
      // default "near you" list reorders once coordinates arrive — but only
      // when geo actually changes the query (see effectiveGeoCoords).
      watch: [locationSlug, searchQuery, effectiveGeoCoords, searchEngine],
    }
  )

  async function searchLocalities(query: string) {
    searchQuery.value = query
    return await execute()
  }

  async function searchLocalitiesWithOptionalLocationFilter(slug: string) {
    locationSlug.value = slug
    return await execute()
  }

  return {
    searchEngineLocalities,
    setLocationSlug: (slugVal: string) => {
      locationSlug.value = slugVal
    },
    searchLocalities,
    searchLocalitiesWithOptionalLocationFilter,
  }
}
